// ----------------------------------------------------------------------- react
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- internos
import { Corregir } from "./Comprobantes";
import { esImagen, firmarPapel } from "../services/papelComprobante";
import {
  FaltaMigracion,
  marcarFijo,
  mover,
  traerAutorizan,
  traerBandeja,
  traerConfig,
  traerFijas,
  traerFormasDePago,
  traerPasos,
  traerQuePide,
  traerSectores,
} from "../services/facturasAPagar";
import { actual } from "../services/quien";
import { linkVigente } from "../shared/facturasRemitos";
import {
  alertas,
  ATAJOS_NO_VA,
  botones,
  coincide,
  conSector,
  copiasPara,
  cuandoLlego,
  cuantas,
  cuitConGuiones,
  diferencia,
  enFechas,
  enFiltro,
  fechaFactura,
  fijaDe,
  FILTROS,
  importeParaEscribir,
  leerImporte,
  lineaEstado,
  lineaFija,
  lineaMismaPersona,
  lineaPaso,
  lineasCorregida,
  ME_TOCA,
  motivoRepetida,
  ORDENES,
  ordenar,
  pctTexto,
  pesos,
  POR_LLEGADA,
  proveedorFijo,
  proveedoresPorCuit,
  puedeMarcarFija,
  quienYCuando,
  resumen,
  revisionesParaAutorizar,
  rotuloAutoriza,
  rotuloEstado,
  rotuloProveedor,
  rotuloVerViejas,
  ESTADOS,
  sinViejas,
  textoResumen,
  textoRevision,
  textoSinNuevas,
  textoViejas,
  trabada,
  viejaSinMirar,
  YA_ESTABA_PAGA,
} from "../shared/facturasAPagar";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "./css/comprobantes.css";

/* Proveedores › Facturas a pagar (etapa 1: la bandeja).
 *
 * Nico, 19/10: «un lugar de proveedores donde caigan las facturas igual que en
 * remito […] quién las autorizó y si están pagas o hay que pagarlas y si hay
 * una aclaración de cómo se paga». Las facturas son las de «Facturas y
 * remitos» (WhatsApp y mail): acá no se copia ninguna, se les pone al lado el
 * estado de pago.
 *
 *   Sin mirar → Esperando autorización → A pagar → Pagada
 *             ↘ Ya vino paga → (quien autoriza la ratifica) → Pagada
 *   Rechazada y No va, con motivo.
 *
 * «Pagada» es un control del hub: el pago se sigue cargando en el sistema de
 * siempre. Las reglas (quién puede qué, las repetidas, lo que hay que
 * confirmar) son de la base; esta pantalla las muestra y no deja apretar lo
 * que la base va a rechazar.
 */

const CLAVE_FECHAS = "proveedores.facturas.fechas";

/* El filtro de fechas se recuerda en el navegador, pero SÓLO el «desde»: un
   «hasta» guardado escondería, los días siguientes, todo lo que llegue nuevo
   (y no aparecería ni en los números de los filtros). Sin almacenamiento
   (modo privado, bloqueado) la pantalla anda igual: arranca sin fechas. */
function leerFechas() {
  try {
    const v = JSON.parse(window.localStorage.getItem(CLAVE_FECHAS) || "null");
    return v && typeof v === "object"
      ? { desde: String(v.desde || ""), hasta: "" }
      : { desde: "", hasta: "" };
  } catch {
    return { desde: "", hasta: "" };
  }
}
function guardarFechas(v) {
  try {
    window.localStorage.setItem(CLAVE_FECHAS, JSON.stringify({ desde: v.desde || "" }));
  } catch {
    /* sin almacenamiento: vale para esta vez */
  }
}

/* En un celular los filtros de fecha, proveedor y búsqueda arrancan
   plegados: si no, ocupan toda la primera pantalla y la primera factura queda
   abajo. */
function filtrosAbiertosAlEntrar() {
  try {
    return !window.matchMedia("(max-width: 640px)").matches;
  } catch {
    return true;
  }
}

const unicas = (l) => [...new Set(l)];

/* La fila de la bandeja, como la espera «Corregir» (la de Facturas y remitos). */
const comoComprobante = (f) => ({
  id: f.ref_id, tipo: f.tipo, proveedor: f.proveedor, cuit: f.cuit, fecha: f.fecha,
  numero: f.numero, importe: f.importe, moneda: f.moneda, detalle: f.detalle,
  alcance: f.alcance, dudas: f.dudas,
});

const Dato = ({ rotulo, valor, vacio = "no lo pude leer" }) => (
  <div className="cmp-dato">
    <span className="cmp-rotulo">{rotulo}</span>
    {valor === null || valor === undefined || valor === "" ? (
      <span className="tenue">{vacio}</span>
    ) : (
      <b>{valor}</b>
    )}
  </div>
);

/* ── El formulario de cada paso, al lado del papel (como «Corregir») ───────── */

const PIDE_MOTIVO = {
  vino_paga: "¿Cómo vino paga?",
  no_va: "¿Por qué no va?",
  es_repetida: "¿Por qué es la repetida?",
  son_distintas: "¿Por qué son de proveedores distintos?",
  rechazar: "¿Por qué no se paga?",
  no_ratificar: "¿Por qué no?",
  deshacer_pagada: "¿Por qué no estaba pagada?",
  reabrir: "¿Por qué?",
};

/* Los pasos que ponen plata sobre la factura: si no tiene CUIT o número, se
   escriben del papel (si no, su foto no traba a la copia que llegue leída). */
const PIDEN_CLAVE = ["autorizar", "vino_paga", "ratificar"];

function Paso({ f, accion, formas, autorizan, onEnviar, onCancelar }) {
  const copias = copiasPara(f, accion);
  const [otra, setOtra] = useState(copias[0]?.key || "");
  const [motivo, setMotivo] = useState(
    accion === "es_repetida" && copias[0] ? motivoRepetida(copias[0]) : "");
  const [nota, setNota] = useState("");
  /* Arranca sin nadie elegido: con la lista ordenada por sector, el primero
     es el del primer sector (Taller de Camiones), y dejarlo puesto mandaba
     a pedírsela a él sin que nadie lo eligiera. */
  const [para, setPara] = useState("");
  const [importe, setImporte] = useState(() => importeParaEscribir(f.importe));
  const [moneda, setMoneda] = useState("");
  const [forma, setForma] = useState("");
  const [cuit, setCuit] = useState("");
  const [numero, setNumero] = useState("");
  const [marcadas, setMarcadas] = useState([]);
  /* Lo que dice la base para el CUIT y el número tipeados (una sin clave): la
     lista de casillas, el bloqueo si choca con otra copia y la razón social
     si el CUIT es nuestro. null = todavía no se preguntó. */
  const [conLoTipeado, setConLoTipeado] = useState(null);
  /* Lo que la base contestó que falta al mandar (por si cambió algo entre
     que se miró y se mandó). Se suman como casillas. */
  const [faltan, setFaltan] = useState([]);
  const [error, setError] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const monedaFija = ["ARS", "USD"].includes(f.moneda_norm) ? f.moneda_norm : null;
  const pideClave = PIDEN_CLAVE.includes(accion) && f.sin_clave && !f.clave_manual;
  const base = conLoTipeado?.revisiones || null;
  const revisiones = unicas([
    ...(accion === "autorizar" ? revisionesParaAutorizar(f, importe, base)
      : accion === "ratificar" ? (base || f.revisiones_ratificar || []) : []),
    ...faltan,
  ]);
  const activas = formas.filter((x) => x.activa);
  const elegida = copias.find((g) => g.key === otra) || copias[0];

  /* Mientras se escriben el CUIT y el número, se le pregunta a la base qué va
     a pedir con ESOS datos (la bandeja lo calculó sin ellos). */
  useEffect(() => {
    if (!pideClave) return undefined;
    if (!cuit.trim() || !numero.trim()) {
      setConLoTipeado(null);
      return undefined;
    }
    let vivo = true;
    const t = setTimeout(() => {
      traerQuePide(f, accion, cuit.trim(), numero.trim())
        .then((r) => vivo && setConLoTipeado(r || null))
        .catch(() => vivo && setConLoTipeado(null));
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [pideClave, f, accion, cuit, numero]);

  const marcar = (r, si) =>
    setMarcadas((m) => (si ? [...new Set([...m, r])] : m.filter((x) => x !== r)));

  const enviar = async () => {
    setError("");
    const datos = {};
    if (PIDE_MOTIVO[accion]) {
      if (!motivo.trim()) { setError("Contá el motivo."); return; }
      datos.motivo = motivo.trim();
    }
    if (nota.trim()) datos.nota = nota.trim();
    if (accion === "pedir") {
      if (!para) { setError("Elegí a quién pedírsela."); return; }
      datos.pedida_a = para;
    }
    if (accion === "es_repetida" || accion === "son_distintas") {
      if (!elegida) { setError("Decí de cuál es copia."); return; }
      datos.otra = { origen: elegida.origen, ref_id: elegida.ref_id };
    }
    let n = null;
    if (accion === "autorizar") {
      n = leerImporte(importe);
      if (!(n > 0)) { setError("Falta el importe autorizado."); return; }
      datos.importe = n;
      if (!monedaFija) {
        if (!moneda) { setError("Elegí la moneda."); return; }
        datos.moneda = moneda;
      }
      if (!forma) { setError("Elegí cómo se paga."); return; }
      datos.forma_pago = forma;
    }
    /* Lo que va a pedir la base con lo tipeado: se pregunta ANTES de mandar,
       así la casilla aparece acá y no queda trabada sin saber por qué. */
    let lista = base;
    if (pideClave) {
      if (!cuit.trim() || !numero.trim()) { setError("Escribí el CUIT y el número del papel."); return; }
      datos.cuit = cuit.trim();
      datos.numero = numero.trim();
      if (accion !== "vino_paga") {
        try {
          const r = await traerQuePide(f, accion, datos.cuit, datos.numero);
          setConLoTipeado(r || null);
          lista = r?.revisiones || [];
        } catch (e) {
          setError(e?.message || "No se pudo consultar.");
          return;
        }
      }
    }
    if (accion === "autorizar") {
      const pide = unicas([...revisionesParaAutorizar(f, String(n), lista), ...faltan]);
      if (pide.some((r) => !marcadas.includes(r))) { setError("Falta confirmar lo de arriba."); return; }
      if (pide.includes("importe_distinto") && !nota.trim()) {
        setError("Si autorizás otro importe, escribí la aclaración.");
        return;
      }
      datos.revisiones = pide;
    }
    if (accion === "ratificar") {
      const pide = unicas([...(lista || f.revisiones_ratificar || []), ...faltan]);
      if (pide.some((r) => !marcadas.includes(r))) { setError("Falta confirmar lo de arriba."); return; }
      datos.revisiones = pide;
    }
    setTrabajando(true);
    const mal = await onEnviar(datos);
    setTrabajando(false);
    if (mal?.faltan?.length) {
      setFaltan((l) => unicas([...l, ...mal.faltan]));
      setError("Falta confirmar lo de arriba.");
    } else if (mal) {
      setError(mal.texto || "No se pudo guardar.");
    }
  };

  const confirmar = {
    pagada: "Sí, está pagada",
    ratificar: "Sí, ratifico",
    autorizar: "Autorizar",
  }[accion] || "Guardar";

  return (
    <div className="cmp-editar" role="group" aria-label="Paso">
      {accion === "pagada" && (
        <p className="fap-pregunta">
          ¿La marcás pagada? {pesos(f.pago?.importe_autorizado, f.pago?.moneda_autorizada)} a{" "}
          {f.proveedor || "este proveedor"}. El pago se carga en el sistema de siempre: esto es el
          control del hub.
        </p>
      )}
      {accion === "ratificar" && (
        <p className="fap-pregunta">¿Ratificás que vino paga? “{f.pago?.vino_paga_motivo}”</p>
      )}

      {accion === "pedir" && (
        <label className="cmp-campo">
          ¿A quién?
          <select value={para} aria-label="¿A quién?" onChange={(e) => setPara(e.target.value)}>
            <option value="">Elegí…</option>
            {autorizan.map((a) => <option key={a.mail} value={a.mail}>{rotuloAutoriza(a)}</option>)}
          </select>
        </label>
      )}

      {(accion === "es_repetida" || accion === "son_distintas") && copias.length > 1 && (
        <label className="cmp-campo">
          {accion === "es_repetida" ? "¿De cuál es copia?" : "¿Con cuál?"}
          <select value={otra} aria-label="La otra copia"
                  onChange={(e) => {
                    setOtra(e.target.value);
                    const g = copias.find((x) => x.key === e.target.value);
                    if (accion === "es_repetida" && g) setMotivo(motivoRepetida(g));
                  }}>
            {copias.map((g) => <option key={g.key} value={g.key}>{quienYCuando(g)}</option>)}
          </select>
        </label>
      )}

      {accion === "autorizar" && (
        <>
          <label className="cmp-campo">
            Importe autorizado
            <input type="text" inputMode="decimal" value={importe} aria-label="Importe autorizado"
                   placeholder="Ej.: 1.234.567,89"
                   onChange={(e) => setImporte(e.target.value)} />
          </label>
          {monedaFija ? (
            <p className="cmp-pie tenue">Moneda: {monedaFija === "USD" ? "dólares" : "pesos"}</p>
          ) : (
            <label className="cmp-campo">
              Moneda
              <select value={moneda} aria-label="Moneda" onChange={(e) => setMoneda(e.target.value)}>
                <option value="">Elegí…</option>
                <option value="ARS">Pesos</option>
                <option value="USD">Dólares</option>
              </select>
            </label>
          )}
          <label className="cmp-campo">
            Cómo se paga
            <select value={forma} aria-label="Cómo se paga" onChange={(e) => setForma(e.target.value)}>
              <option value="">Elegí…</option>
              {activas.map((x) => <option key={x.clave} value={x.clave}>{x.etiqueta}</option>)}
            </select>
          </label>
        </>
      )}

      {pideClave && (
        <>
          <p className="cmp-pie tenue">No se leyó el CUIT o el número: escribilos como dice el papel.</p>
          <label className="cmp-campo">
            CUIT
            <input type="text" value={cuit} aria-label="CUIT" placeholder="Como dice el papel"
                   onChange={(e) => setCuit(e.target.value)} />
          </label>
          <label className="cmp-campo">
            Número
            <input type="text" value={numero} aria-label="Número" placeholder="0001-00001234"
                   onChange={(e) => setNumero(e.target.value)} />
          </label>
          {conLoTipeado?.bloqueo && (
            <p className="fap-alerta fap-alerta--mal">Con ese CUIT y ese número: {conLoTipeado.bloqueo}</p>
          )}
        </>
      )}

      {accion === "autorizar" && diferencia(f, importe) && (
        <p className="fap-pregunta">{diferencia(f, importe)}</p>
      )}

      {revisiones.map((r) => {
        const texto = textoRevision(r, f, { importe, cuitNuestro: conLoTipeado?.cuit_nuestro });
        return (
          <label key={r} className="cmp-campo cmp-campo--check">
            <input type="checkbox" checked={marcadas.includes(r)} aria-label={texto}
                   onChange={(e) => marcar(r, e.target.checked)} />
            {" "}{texto}
          </label>
        );
      })}

      {accion === "no_va" && (
        <div className="cmp-tipos" role="group" aria-label="Atajos">
          {ATAJOS_NO_VA.map((t) => (
            <button key={t} type="button" className="cmp-seg" onClick={() => setMotivo(t)}>{t}</button>
          ))}
        </div>
      )}
      {accion === "vino_paga" && f.antes_del_arranque && (
        <div className="cmp-tipos">
          <button type="button" className="cmp-seg" onClick={() => setMotivo(YA_ESTABA_PAGA)}>
            {YA_ESTABA_PAGA}
          </button>
        </div>
      )}

      {PIDE_MOTIVO[accion] && (
        <label className="cmp-campo">
          {PIDE_MOTIVO[accion]}
          <textarea rows={2} value={motivo} aria-label={PIDE_MOTIVO[accion]}
                    onChange={(e) => setMotivo(e.target.value)} />
        </label>
      )}
      {accion === "vino_paga" && <p className="cmp-pie tenue">Queda para que la ratifique quien autoriza.</p>}
      {accion === "no_ratificar" && <p className="cmp-pie tenue">Queda esperando autorización, pedida a vos.</p>}

      {["pedir", "autorizar", "pagada"].includes(accion) && (
        <label className="cmp-campo">
          {accion === "pedir" ? "Algo para decirle (opcional)"
            : accion === "autorizar" ? "Aclaración" : "Nota (opcional)"}
          <input type="text" value={nota}
                 aria-label={accion === "autorizar" ? "Aclaración" : "Nota"}
                 placeholder={accion === "autorizar" ? "Ej.: en dos e-cheq a 30 y 60 días, desde TCC" : ""}
                 onChange={(e) => setNota(e.target.value)} />
        </label>
      )}

      {error && <p className="cmp-aviso">{error}</p>}

      <div className="cmp-acciones">
        <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo" onClick={onCancelar}>
          Cancelar
        </button>
        <button type="button" className="tk-boton tk-boton--chico" disabled={trabajando} onClick={enviar}>
          {trabajando ? "Guardando…" : confirmar}
        </button>
      </div>
    </div>
  );
}

/* ── Marcar al proveedor como fijo ────────────────────────────────────────

   Vale para el PROVEEDOR (su CUIT), no para esta factura: desde ahí, todas
   las suyas —las que llegaron y las que lleguen— se ven como fijas. */

function MarcarFija({ f, fijo, categorias, onListo, onCancelar }) {
  const [categoria, setCategoria] = useState(fijo?.categoria || "");
  const [nota, setNota] = useState("");
  const [error, setError] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const guardar = async () => {
    setError("");
    if (!fijo && !categoria) { setError("Elegí qué tipo de fija es."); return; }
    setTrabajando(true);
    try {
      await marcarFijo(f.cuit11, categoria, nota.trim());
      onListo();
    } catch (e) {
      setError(e?.message || "No se pudo guardar.");
      setTrabajando(false);
    }
  };

  return (
    <div className="cmp-editar" role="group" aria-label="Proveedor fijo">
      <p className="fap-pregunta">
        {f.proveedor || "Este proveedor"} · CUIT {cuitConGuiones(f.cuit11)}: todas sus facturas.
      </p>
      <label className="cmp-campo">
        ¿Qué fija es?
        <select value={categoria} aria-label="¿Qué fija es?" onChange={(e) => setCategoria(e.target.value)}>
          {fijo ? <option value="">No es fija</option> : <option value="">Elegí…</option>}
          {categorias.map((c) => <option key={c.clave} value={c.clave}>{c.etiqueta}</option>)}
        </select>
      </label>
      <label className="cmp-campo">
        Nota (opcional)
        <input type="text" value={nota} aria-label="Nota" onChange={(e) => setNota(e.target.value)} />
      </label>
      {error && <p className="cmp-aviso">{error}</p>}
      <div className="cmp-acciones">
        <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo" onClick={onCancelar}>
          Cancelar
        </button>
        <button type="button" className="tk-boton tk-boton--chico" disabled={trabajando} onClick={guardar}>
          {trabajando ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

/* ── La pantalla ─────────────────────────────────────────────────────────── */

const FacturasAPagar = () => {
  const { permisos = null, verComoMail = null } = useOutletContext() || {};
  const puedeVer = permisos === null || permisos.includes("ver.proveedores");
  const cargar = permisos !== null && permisos.includes("cargar.proveedores");
  const autorizar = permisos !== null && permisos.includes("autorizar.proveedores");
  const [params, setParams] = useSearchParams();
  const verKey = params.get("ver") || "";
  const yo = String(actual()?.mail || "").toLowerCase();

  const [filas, setFilas] = useState(undefined);
  const [falla, setFalla] = useState(null);
  const [formas, setFormas] = useState([]);
  /* null = todavía viene. Mientras, «Pedir autorización a…» no se muestra:
     si no, a Nico (el único que autoriza) le aparecía y se borraba. */
  const [autorizan, setAutorizan] = useState(null);
  /* El sector de cada uno (sólo para ordenar y rotular): [] si no hay. */
  const [sectores, setSectores] = useState([]);
  const [config, setConfig] = useState(null);
  /* Las fijas: null = la base no contestó (o todavía no): sin marcas. */
  const [fijas, setFijas] = useState(null);
  const [filtro, setFiltro] = useState("pendientes");
  const [fechas, setFechas] = useState(leerFechas);
  const [cuit, setCuit] = useState("");
  const [busca, setBusca] = useState("");
  const [orden, setOrden] = useState(POR_LLEGADA);
  /* Las de antes del arranque que nadie movió (Nico, 20/10: «no siguieron
     ningún circuito»): ocultas salvo que se pidan. No se recuerda entre
     visitas: cada vez que se entra, arrancan ocultas. */
  const [verViejas, setVerViejas] = useState(false);
  const [links, setLinks] = useState({});
  /* Los papeles ya firmados, para no volver a firmarlos todos en cada paso
     (antes, cada clic eran ~89 firmas de Storage). */
  const linksRef = useRef({});
  linksRef.current = links;
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(filtrosAbiertosAlEntrar);
  const [aviso, setAviso] = useState("");
  const [abierto, setAbierto] = useState(null);
  const [corrigiendo, setCorrigiendo] = useState(null);
  const [pasos, setPasos] = useState({});
  const [vuelta, setVuelta] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 60 * 1000);
    return () => clearInterval(reloj);
  }, []);

  useEffect(() => {
    if (!puedeVer) return undefined;
    let vivo = true;
    traerBandeja()
      .then((f) => {
        if (!vivo) return;
        setFilas(f);
        setFalla(null);
        f.forEach((x) => {
          if (!x.archivo) return;
          const ya = linksRef.current[x.key];
          if (ya && ya.archivo === x.archivo && linkVigente(ya.vence, Date.now())) return;
          Promise.resolve(firmarPapel(x.origen, x.archivo))
            .then((l) => vivo && l && setLinks((p) => ({ ...p, [x.key]: { ...l, archivo: x.archivo } })))
            .catch(() => {});
        });
      })
      .catch((e) => {
        if (!vivo) return;
        if (e instanceof FaltaMigracion || e?.name === "FaltaMigracion") setFalla({ tipo: "migracion" });
        else if (e?.code === "42501") setFalla({ tipo: verComoMail ? "ver-como" : "puerta" });
        else setFalla({ tipo: "otro", texto: e?.message });
      });
    /* Aparte y sin trabar la bandeja: si falla, no hay marcas de fija. */
    traerFijas().then((x) => vivo && setFijas(x || null)).catch(() => vivo && setFijas(null));
    return () => { vivo = false; };
  }, [vuelta, puedeVer, verComoMail]);

  useEffect(() => {
    if (!puedeVer) return;
    traerFormasDePago().then(setFormas).catch(() => setFormas([]));
    traerConfig().then(setConfig).catch(() => setConfig(null));
    if (cargar || autorizar) {
      traerAutorizan().then((l) => setAutorizan(l || [])).catch(() => setAutorizan([]));
      traerSectores().then((l) => setSectores(l || [])).catch(() => setSectores([]));
    }
  }, [puedeVer, cargar, autorizar]);

  const recargar = () => {
    setAbierto(null);
    setCorrigiendo(null);
    setPasos({});
    setVuelta((v) => v + 1);
  };

  const cambiarFechas = (v) => { setFechas(v); guardarFechas(v); };

  const verLa = (key) => {
    const p = new URLSearchParams(params);
    if (key) p.set("ver", key); else p.delete("ver");
    setParams(p);
  };

  const abrirPapel = async (e, f) => {
    if (linkVigente(links[f.key]?.vence, Date.now())) return;
    e.preventDefault();
    const ventana = window.open("", "_blank");
    if (ventana) ventana.opener = null;
    const l = await firmarPapel(f.origen, f.archivo);
    if (l) setLinks((p) => ({ ...p, [f.key]: { ...l, archivo: f.archivo } }));
    if (!l) {
      if (ventana) ventana.close();
      setAviso("No pude abrir el papel. Recargá la página.");
    } else if (ventana) {
      ventana.location.href = l.url;
    }
  };

  /* Mover: si la base dice que no, el porqué queda en el formulario (y si
     falta confirmar algo, la lista: el formulario suma esas casillas). Si
     alguien la movió o la corrigió mientras se miraba (40001), la lista se
     recarga sola. */
  const hacer = async (f, accion, datos) => {
    setAviso("");
    try {
      await mover(f, accion, datos);
      recargar();
      return null;
    } catch (e) {
      if (e?.code === "40001") {
        setAviso("Cambió mientras la mirabas: fijate cómo quedó.");
        recargar();
        return null;
      }
      return { texto: e?.message || "No se pudo guardar.", faltan: e?.faltan || [] };
    }
  };

  const verPasos = (f) => {
    if (pasos[f.key]) {
      setPasos((p) => { const c = { ...p }; delete c[f.key]; return c; });
      return;
    }
    setPasos((p) => ({ ...p, [f.key]: "cargando" }));
    traerPasos(f.origen, f.ref_id)
      .then((l) => setPasos((p) => ({ ...p, [f.key]: l })))
      .catch(() => setPasos((p) => ({ ...p, [f.key]: "error" })));
  };

  const ctx = useMemo(() => ({ yo }), [yo]);
  const porSector = useMemo(() => conSector(autorizan || [], sectores), [autorizan, sectores]);
  const nombres = useMemo(
    () => Object.fromEntries((autorizan || []).map((a) => [a.mail, a.nombre])), [autorizan]);
  /* null = no se sabe todavía. Sin nadie más que quien mira, no hay a quién
     pedírsela. */
  const unicoQueAutoriza = autorizan === null ? null : !autorizan.some((a) => a.mail !== yo);

  /* La bandeja: sin las viejas que nadie movió (salvo «Ver igual»). De acá
     salen la lista, los números de los filtros y los proveedores. `?ver=`
     (y «Ver la otra») busca en TODAS: una copia vieja no queda inalcanzable. */
  const enBandeja = useMemo(() => sinViejas(filas, verViejas), [filas, verViejas]);
  const recortadas = useMemo(
    () => enBandeja.filter((f) => enFechas(f, fechas.desde, fechas.hasta)
      && (!cuit || (f.cuit11 || "sin-cuit") === cuit)
      && coincide(f, busca)),
    [enBandeja, fechas, cuit, busca]);
  const numeros = useMemo(() => cuantas(recortadas, ctx), [recortadas, ctx]);
  const visibles = useMemo(
    () => (verKey
      ? (filas || []).filter((f) => f.key === verKey)
      : ordenar(recortadas.filter((f) => enFiltro(f, filtro, ctx)), orden)),
    [verKey, filas, recortadas, filtro, ctx, orden]);
  const proveedores = useMemo(() => proveedoresPorCuit(enBandeja), [enBandeja]);
  const res = useMemo(() => resumen(filas, config?.arranque, { verViejas }), [filas, config, verViejas]);

  /* Al ocultarlas, si el proveedor elegido sólo tenía de ésas, vuelve a
     «Todos los proveedores»: si no, el filtro queda puesto sin opción a la
     vista y la lista vacía sin saber por qué. */
  const alternarViejas = () => {
    const ver = !verViejas;
    setVerViejas(ver);
    if (!ver && cuit && !proveedoresPorCuit(sinViejas(filas)).some((p) => p.cuit === cuit)) setCuit("");
  };

  if (!puedeVer) return <PuertaCerrada />;
  if (falla?.tipo === "puerta") return <PuertaCerrada />;
  if (falla?.tipo === "ver-como") {
    return (
      <div className="panel-aviso suave">
        <h3>Con «Ver como» esta pantalla no trae datos</h3>
        <p className="panel-aviso__chico">
          Con «Ver como» esta pantalla no trae datos: la base mira tu cuenta, no la que estás mirando.
        </p>
      </div>
    );
  }
  if (falla?.tipo === "migracion") {
    return (
      <div className="panel-aviso suave"><h3>Esta pantalla todavía no está habilitada.</h3></div>
    );
  }
  if (falla?.tipo === "otro") {
    return <div className="panel-aviso"><h3>No se pudo consultar</h3></div>;
  }

  const filtrosDeEstado = autorizar ? [...FILTROS, ME_TOCA] : FILTROS;
  const cuantosFiltros = [fechas.desde, fechas.hasta, cuit, busca.trim()].filter(Boolean).length;
  /* Con `?ver=` sobre una vieja que nadie movió, la línea de arriba lo aclara. */
  const lineaViejas = textoViejas(res, { mirandoUna: !!verKey && visibles.some(viejaSinMirar) });

  return (
    <div className="cmp">
      {filas !== undefined && (
        <div className="fap-resumen">
          <p>{textoResumen(res)}</p>
          {lineaViejas && (
            <p className="tenue">
              {lineaViejas}{" "}
              <button type="button" className="cmp-link" onClick={alternarViejas}>{rotuloVerViejas(res)}</button>
            </p>
          )}
        </div>
      )}

      <div className="cmp-barra">
        <div className="cmp-tipos fap-estados" role="group" aria-label="Estado">
          {filtrosDeEstado.map(([id, label]) => (
            <button key={id} type="button" aria-pressed={!verKey && filtro === id}
                    className={"cmp-seg" + (!verKey && filtro === id ? " activo" : "")}
                    onClick={() => { setFiltro(id); if (verKey) verLa(""); }}>
              {label} ({numeros[id] ?? 0})
            </button>
          ))}
        </div>
      </div>

      <details className="fap-mas" open={filtrosAbiertos}
               onToggle={(e) => setFiltrosAbiertos(e.currentTarget.open)}>
        <summary>
          Fechas, proveedor y búsqueda
          {cuantosFiltros > 0 ? ` (${cuantosFiltros} puesto${cuantosFiltros === 1 ? "" : "s"})` : ""}
        </summary>
      <div className="cmp-barra fap-fechas">
        <label>
          Llegaron desde{" "}
          <input type="date" className="cmp-busca" value={fechas.desde} aria-label="Llegaron desde"
                 onChange={(e) => cambiarFechas({ ...fechas, desde: e.target.value })} />
        </label>
        <label>
          hasta{" "}
          <input type="date" className="cmp-busca" value={fechas.hasta} aria-label="Hasta"
                 onChange={(e) => cambiarFechas({ ...fechas, hasta: e.target.value })} />
        </label>
        <button type="button" className="cmp-seg" onClick={() => cambiarFechas({ desde: "", hasta: "" })}>
          Todas las fechas
        </button>
        <select className="cmp-busca" value={cuit} aria-label="Proveedor"
                onChange={(e) => setCuit(e.target.value)}>
          <option value="">Todos los proveedores</option>
          {proveedores.map((p) => <option key={p.cuit} value={p.cuit}>{rotuloProveedor(p)}</option>)}
        </select>
        <input type="search" className="cmp-busca" placeholder="Proveedor, número, CUIT, quién…"
               aria-label="Buscar facturas" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <div className="cmp-tipos" role="group" aria-label="Orden">
          {ORDENES.map(([id, label]) => (
            <button key={id} type="button" aria-pressed={orden === id}
                    className={"cmp-seg" + (orden === id ? " activo" : "")} onClick={() => setOrden(id)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      </details>

      {aviso && <p className="cmp-aviso">{aviso}</p>}
      {verKey && (
        <p className="cmp-pie">
          Mostrando una sola factura ·{" "}
          <button type="button" className="cmp-link" onClick={() => verLa("")}>Ver todas</button>
        </p>
      )}

      {filas === undefined && <p className="tenue">Buscando…</p>}

      {filas !== undefined && visibles.length === 0 && (
        !verKey && enBandeja.length === 0 && res.viejasSinMirar > 0 ? (
          <div className="panel-aviso suave">
            <h3>{textoSinNuevas(res)}</h3>
            <p className="panel-aviso__chico">Las de antes no se muestran: están arriba, en «Ver igual».</p>
          </div>
        ) : (
          <div className="panel-aviso suave">
            <h3>No hay facturas con ese filtro</h3>
            <p className="panel-aviso__chico">Probá con otro estado o fecha.</p>
          </div>
        )
      )}

      {visibles.map((f) => {
        const esWA = f.origen === "whatsapp";
        const est = ESTADOS[f.estado] || ESTADOS.sin_mirar;
        const lista = botones(f, { cargar, autorizar, unicoQueAutoriza });
        const linea = lineaEstado(f);
        const misma = lineaMismaPersona(f);
        const dudas = Array.isArray(f.dudas) ? f.dudas : [];
        const pasosDe = pasos[f.key];
        const fija = fijaDe(fijas, f);
        const lineaDeFija = lineaFija(fija, f, fijas?.umbral);
        const marcaFija = puedeMarcarFija(f, fijas, { cargar, autorizar });
        return (
          <div className="cmp-carta" key={f.key} role="group"
               aria-label={`Factura de ${f.proveedor || "proveedor sin leer"}${f.numero ? ` ${f.numero}` : ""}`}>
            <div className="cmp-papel">
              {links[f.key] ? (
                <a href={links[f.key].url} target="_blank" rel="noreferrer" onClick={(e) => abrirPapel(e, f)}>
                  {esImagen(f.origen, f) ? (
                    <img src={links[f.key].url} alt={`Factura de ${f.proveedor || "proveedor sin leer"}`} />
                  ) : (
                    <span className="cmp-sinpapel">Abrir el papel<small>Se abre en otra pestaña</small></span>
                  )}
                </a>
              ) : f.archivo ? (
                <span className="tenue">Abriendo el archivo…</span>
              ) : (
                <span className="cmp-sinpapel">Sin archivo guardado<small>Quedaron sólo los datos</small></span>
              )}
            </div>

            <div className="cmp-datos">
              <div className="cmp-cabeza">
                <b>Factura</b>
                {esWA
                  ? <span className="pill" title="Llegó por WhatsApp">📱 por WhatsApp</span>
                  : <span className="pill" title="Llegó por mail">📧 por mail</span>}
                <span className={"pill " + est.clase}>{rotuloEstado(f.estado)}</span>
                {trabada(f) && <span className="pill mal" title={f.bloqueo || ""}>repetida</span>}
                {fija && <span className="pill info">fija · {fija.etiqueta || fija.categoria}</span>}
                {fija?.sube_mucho && <span className="pill mal">subió {pctTexto(fija.pct)}</span>}
                {f.fuera && <span className="pill gris">salió</span>}
              </div>

              {f.lectura_error && !f.numero ? (
                <p className="cmp-dudas">No la pude leer: abrí el papel.</p>
              ) : (
                <>
                  <Dato rotulo="Proveedor" valor={f.proveedor} />
                  <Dato rotulo="CUIT" valor={f.cuit ? cuitConGuiones(f.cuit) : null} />
                  <Dato rotulo="Número" valor={f.numero} />
                  <Dato rotulo="Fecha" valor={fechaFactura(f.fecha)} />
                  <Dato rotulo="Importe" valor={pesos(f.importe, f.moneda)} />
                </>
              )}
              {f.detalle && <p className="cmp-detalle">{f.detalle}</p>}

              {lineaDeFija && (
                <p className={"fap-alerta fap-alerta--" + lineaDeFija.tono}>{lineaDeFija.texto}</p>
              )}
              {linea && <p className="fap-estado">{linea}</p>}
              {misma && <p className="fap-estado tenue">{misma}</p>}

              {alertas(f).map((a) => (
                <p key={a.id} className={"fap-alerta fap-alerta--" + a.tono}>
                  {a.texto}
                  {a.otra && (
                    <>
                      {" "}
                      <button type="button" className="cmp-link" onClick={() => verLa(a.otra)}>Ver la otra</button>
                    </>
                  )}
                </p>
              ))}
              {dudas.length > 0 && (
                <p className="cmp-dudas">⚠️ El bot no está seguro de: {dudas.join(", ")}.</p>
              )}

              <p className="cmp-pie tenue">
                {esWA
                  ? `La mandó ${f.quien_nombre || "alguien"} · llegó ${cuandoLlego(f.llego, ahora)}`
                  : `Llegó de ${f.remitente_email || "—"} · ${cuandoLlego(f.llego, ahora)}`}
              </p>
              {lineasCorregida(f).map((t) => <p key={t} className="cmp-pie tenue">{t}</p>)}

              {abierto?.key === f.key && abierto.accion === "fija" ? (
                <MarcarFija f={f} fijo={proveedorFijo(fijas, f.cuit11)} categorias={fijas?.categorias || []}
                            onListo={recargar} onCancelar={() => setAbierto(null)} />
              ) : abierto?.key === f.key ? (
                <Paso f={f} accion={abierto.accion} formas={formas} autorizan={porSector}
                      onEnviar={(datos) => hacer(f, abierto.accion, datos)}
                      onCancelar={() => setAbierto(null)} />
              ) : corrigiendo === f.key ? (
                <Corregir f={comoComprobante(f)} onListo={recargar} onCancelar={() => setCorrigiendo(null)} />
              ) : permisos === null ? (
                <p className="cmp-pie tenue">Buscando qué podés hacer…</p>
              ) : (
                <div className="cmp-acciones">
                  {lista.map((b) => (
                    <button key={b.id} type="button"
                            className={"tk-boton tk-boton--chico" + (b.id === "autorizar" ? "" : " tk-boton--flojo")}
                            disabled={!!b.traba} title={b.traba || b.title || ""}
                            onClick={() => { setAviso(""); setAbierto({ key: f.key, accion: b.id }); }}>
                      {b.rotulo}
                    </button>
                  ))}
                  {marcaFija && (
                    <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => { setAviso(""); setAbierto({ key: f.key, accion: "fija" }); }}>
                      {proveedorFijo(fijas, f.cuit11) ? "Cambiar fija" : "Es fija…"}
                    </button>
                  )}
                  {f.puedo_corregir && (
                    <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => setCorrigiendo(f.key)}>
                      ✎ Corregir
                    </button>
                  )}
                  <button type="button" className="cmp-link" onClick={() => verPasos(f)}>
                    {pasosDe ? "Ocultar los pasos" : "Ver los pasos"}
                  </button>
                </div>
              )}
              {!f.puedo_corregir && abierto?.key !== f.key && (
                <p className="cmp-pie tenue">
                  {!esWA
                    ? "Ésta no se corrige en el hub: mirá el papel."
                    : f.quien_nombre
                      ? `Si está mal leída, avisale a ${f.quien_nombre}, que la mandó.`
                      : "Si está mal leída, avisale a quien la mandó."}
                </p>
              )}
              {lista.some((b) => b.traba) && abierto?.key !== f.key && (
                <p className="cmp-pie tenue">{f.bloqueo}</p>
              )}

              {pasosDe === "cargando" && <p className="tenue">Buscando los pasos…</p>}
              {pasosDe === "error" && <p className="cmp-aviso">No pude traer los pasos.</p>}
              {Array.isArray(pasosDe) && (
                <ul className="fap-pasos">
                  {pasosDe.length === 0 && <li className="tenue">Todavía no la movió nadie.</li>}
                  {pasosDe.map((p) => <li key={p.id}>{lineaPaso(p, { formas, nombres })}</li>)}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FacturasAPagar;
